import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import DataDisplay from '../../components/DataDisplay';
import { API } from '../../api/api_setup';
import { useAuth } from '../../hooks/useAuth';


const PolicyLagPanel = ({ clusterName }) => {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [grouped, setGrouped] = useState({})
  const [topic, setTopic] = useState('All')
  const [selectedDate, setSelectedDate] = useState(null)

  const columns = [
    { field: 'timestamp', headerName: 'Timestamp', width: 220 },
    { field: 'topic', headerName: 'Topic', width: 320 },
    { field: 'current_lag', headerName: 'Current Lag', type: 'number', width: 140 },
    { field: 'forward_progress', headerName: 'Forward Progress', type: 'number', width: 170 },
    { field: 'progress', headerName: 'Progress', type: 'number', width: 140 },
    { field: 'eta_secs_remaining', headerName: 'ETA (secs)', type: 'number', width: 130 },
    { field: 'eta_remaining_str', headerName: 'ETA (Time)', width: 150 },
    { field: 'published', headerName: 'Published', type: 'number', width: 140 },
    { field: 'total_messages', headerName: 'Total Messages', type: 'number', width: 150 },
  ]

  useEffect(() => {
    setIsLoading(true)
    const headers = { Authorization: `Bearer ${user.token}` }
    API.get(`/cluster_debug/policy_lag/${clusterName}?days=7`, { headers })
      .then((allRes) => {
        const data = allRes.data.data || []
        setRows(data)
        const groupedData = data.reduce((acc, row) => {
          const key = row.topic || 'Unknown'
          acc[key] = acc[key] || []
          acc[key].push(row)
          return acc
        }, {})
        setGrouped(groupedData)
        setTopic((prev) => {
          if (prev !== 'All' && groupedData[prev]) {
            return prev
          }
          const keys = Object.keys(groupedData)
          if (prev === 'All' && keys.length > 0) {
            return keys[0]
          }
          if (prev !== 'All' && !groupedData[prev]) {
            return keys[0] || 'All'
          }
          return prev
        })
      })
      .catch(() => { })
      .finally(() => setIsLoading(false))
  }, [clusterName, user.token])

  const filteredRows = useMemo(() => {
    let filtered = rows
    const selectedDateStr = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null

    if (selectedDateStr) {
      filtered = filtered.filter((row) => {
        if (!row.timestamp) return false
        const rowDateStr = String(row.timestamp).slice(0, 10)
        return rowDateStr === selectedDateStr
      })
    }

    if (topic !== 'All') {
      filtered = filtered.filter((row) => row.topic === topic)
    }

    return filtered
  }, [rows, selectedDate, topic])

  return (
    <>
      <Grid container alignItems={'center'}>
        <Grid item sx={{ paddingLeft: '10px', paddingRight: '20px' }}>
          <Typography variant='h6' color={'#313638'}>{'Policy lag'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <Autocomplete
            options={["All", ...Object.keys(grouped)]}
            value={topic}
            onChange={(e, newValue) => setTopic(newValue || 'All')}
            renderInput={(params) => <TextField {...params} label="Topic" size='small' />}
            filterSelectedOptions
            disableClearable
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <DatePicker
            label="Filter by Date"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid item lg={12} md={12} sx={{ paddingBottom: '16px' }}>
        <DataDisplay
          rows={filteredRows.map((r, idx) => ({ id: idx + 1, ...r }))}
          columns={columns}
          loading={isLoading}
          sx={{ margin: '10px', marginBottom: '16px' }}
          density='compact'
        />
      </Grid>
    </>
  );
}

export default PolicyLagPanel;
